'use client'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { PlayMessageData } from '@/lib/pbx/flow/types'
import { useTranslations } from "next-intl"


interface PlayMessagePanelProps {
  data: PlayMessageData
  onUpdate: (data: Partial<PlayMessageData>) => void
}

function getLANGUAGES(tUi: ReturnType<typeof useTranslations<"Pbx.PbxFlowEditorPanelsPlayMessagePanel">>) {
  return [
  { value: 'en-US', label: tUi("englishUS") },
  { value: 'en-GB', label: tUi("englishUK") },
  { value: 'de-DE', label: tUi("german") },
  { value: 'fr-FR', label: tUi("french") },
  { value: 'es-ES', label: tUi("spanish") },
  { value: 'pl-PL', label: tUi("polish") },
  { value: 'uk-UA', label: tUi("ukrainian") },
  { value: 'ru-RU', label: tUi("russian") },
]
}

function getVOICES(tUi: ReturnType<typeof useTranslations<"Pbx.PbxFlowEditorPanelsPlayMessagePanel">>) {
  return [
  { value: 'alice', label: tUi("aliceFemale") },
  { value: 'Polly.Tatyana', label: tUi("tatyanaFemaleRU") },
  { value: 'Polly.Joanna', label: tUi("joannaFemaleEN") },
  { value: 'Polly.Matthew', label: tUi("matthewMaleEN") },
]
}

export function PlayMessagePanel({ data, onUpdate }: PlayMessagePanelProps) {
  const tUi = useTranslations("Pbx.PbxFlowEditorPanelsPlayMessagePanel")
  const LANGUAGES = getLANGUAGES(tUi)
  const VOICES = getVOICES(tUi)


  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label><span key={tUi("messageType")}>{tUi("messageType")}</span></Label>
        <Select
          value={data.message_type || 'tts'}
          onValueChange={(value: 'tts' | 'audio') =>
            onUpdate({ message_type: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tts"><span key={tUi("textToSpeech")}>{tUi("textToSpeech")}</span></SelectItem>
            <SelectItem value="audio"><span key={tUi("audioFile")}>{tUi("audioFile")}</span></SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data.message_type === 'tts' ? (
        <>
          <div className="space-y-2">
            <Label htmlFor="tts_text"><span key={tUi("text")}>{tUi("text")}</span></Label>
            <Textarea
              id="tts_text"
              value={data.tts_text || ''}
              onChange={(e) => onUpdate({ tts_text: e.target.value })}
              placeholder={tUi("enterTextToSpeak")}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label><span key={tUi("language")}>{tUi("language")}</span></Label>
            <Select
              value={data.tts_language || 'en-US'}
              onValueChange={(value) => onUpdate({ tts_language: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label><span key={tUi("voice")}>{tUi("voice")}</span></Label>
            <Select
              value={data.tts_voice || 'alice'}
              onValueChange={(value) => onUpdate({ tts_voice: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VOICES.map((voice) => (
                  <SelectItem key={voice.value} value={voice.value}>
                    {voice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="audio_url"><span key={tUi("audioURL")}>{tUi("audioURL")}</span></Label>
          <Input
            id="audio_url"
            value={data.audio_url || ''}
            onChange={(e) => onUpdate({ audio_url: e.target.value })}
            placeholder={"https://example.com/message.mp3"}
          />
          <p className="text-xs text-muted-foreground">
            <span key={tUi("directURLToMP3OrWAVFile")}>{tUi("directURLToMP3OrWAVFile")}</span></p>
        </div>
      )}
    </div>
  )
}
